import { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useOrgStore } from '@/stores/orgStore'
import { usePublishPageData } from '@/stores/pageDataStore'
import { assetsApi } from '@/api/clients/assetsApi'
import type { AssetListItem, Band } from '@/api/types'
import { PageHeader, Card, Button, SelectInput } from '@/components/common/ui'
import { BandBadge } from '@/components/common/Badge'
import { QueryState } from '@/components/common/State'
import { TreemapLens } from '@/components/lenses/TreemapLens'
import { SunburstLens } from '@/components/lenses/SunburstLens'
import { HeatmapLens } from '@/components/lenses/HeatmapLens'
import { GraphView } from '@/pages/Graph'
import { useViewStore, type Lens } from '@/stores/viewStore'

const LENSES: { key: Lens; label: string; icon: string }[] = [
  { key: 'table', label: 'Prioritised Table', icon: 'table_rows' },
  { key: 'treemap', label: 'Treemap ($)', icon: 'dashboard' },
  { key: 'sunburst', label: 'Sunburst', icon: 'donut_large' },
  { key: 'heatmap', label: 'Heatmap', icon: 'grid_on' },
  { key: 'graph', label: 'Risk Graph', icon: 'hub' },
]

const BANDS: Band[] = ['critical', 'high', 'medium', 'low', 'info']
const PAGE_SIZE = 50

export default function Assets() {
  const { currentOrgId: orgId } = useOrgStore()
  // Lens + band come from the shared view store (driven by lens tabs, saved
  // profiles, or persona switch — §5.8 multi-lens / P4).
  const lens = useViewStore((s) => s.lens)
  const setLens = useViewStore((s) => s.setLens)
  const band = useViewStore((s) => s.band)
  const setBand = useViewStore((s) => s.setBand)
  const navigate = useNavigate()
  const [offset, setOffset] = useState(0)
  const [pages, setPages] = useState<AssetListItem[][]>([])
  const [hasMore, setHasMore] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const isFetchingRef = useRef(false)

  const assets = useQuery({
    queryKey: ['assets', orgId, band, offset],
    queryFn: () =>
      assetsApi.list(orgId, {
        limit: PAGE_SIZE,
        offset,
        band: band || undefined,
      }),
    enabled: !!orgId,
  })

  // Reset paging when the band filter (or org) changes. Don't clear pages — the sync
  // effect uses offset===0 as a "replace" signal, avoiding an empty-table flash.
  useEffect(() => {
    setOffset(0)
    setHasMore(false)
  }, [band, orgId])

  // Sync fetched page → local accumulator. offset===0 replaces (new filter/first page);
  // a full page implies more exist (the list API returns no total).
  useEffect(() => {
    if (!assets.data) return
    const batch = assets.data
    setPages((prev) => {
      if (offset === 0) return [batch]
      const next = [...prev]
      next[offset / PAGE_SIZE] = batch
      return next
    })
    setHasMore(batch.length === PAGE_SIZE)
  }, [assets.data, offset])

  const allAssets = useMemo(() => {
    const seen = new Set<string>()
    return pages.flat().filter((a) => {
      if (seen.has(a.id)) return false
      seen.add(a.id)
      return true
    })
  }, [pages])

  // Publish a compact snapshot for the assistant ("explain this page").
  usePublishPageData(
    useMemo(
      () => ({
        page: 'Prioritised Assets worklist',
        lens,
        band_filter: band || 'all',
        loaded: allAssets.length,
        top_assets: allAssets
          .slice(0, 30)
          .map((a) => ({
            name: a.name,
            band: a.band,
            score: a.score,
            algorithm: a.algorithm,
            key_size: a.key_size,
            hndl_window: a.hndl_window,
          })),
      }),
      [lens, band, allAssets]
    )
  )

  // Infinite scroll: load the next page when the sentinel nears the viewport.
  isFetchingRef.current = assets.isFetching
  const loadMore = useCallback(() => setOffset((o) => o + PAGE_SIZE), [])
  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingRef.current) loadMore()
      },
      { rootMargin: '200px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasMore, loadMore])

  const exportCsv = () => {
    const rows = allAssets
    const header =
      'name,external_ref,algorithm,inventory,source,asset_type,band,score,hndl_window,recommended_target\n'
    const body = rows
      .map(
        (a) =>
          `"${a.name}","${a.external_ref ?? ''}","${a.algorithm}","${a.inventory_name ?? ''}","${a.source_name ?? ''}","${a.asset_type}","${a.band}",${a.score},${a.hndl_window ?? ''},"${a.recommended_target ?? ''}"`
      )
      .join('\n')
    const blob = new Blob([header + body], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'prioritised-assets.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div>
      <PageHeader
        title="Prioritised Assets"
        subtitle="Ranked worklist — band → HNDL urgency → $"
        actions={
          lens === 'table' && (
            <Button icon="download" onClick={exportCsv}>
              Export CSV (M3 / ticketing)
            </Button>
          )
        }
      />

      {/* Lens tab bar */}
      <div className="flex gap-1 border-b border-border-light dark:border-border-dark mb-5 overflow-x-auto">
        {LENSES.map((l) => (
          <button
            key={l.key}
            onClick={() => setLens(l.key)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
              lens === l.key
                ? 'border-primary text-primary dark:border-secondary dark:text-secondary'
                : 'border-transparent text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark'
            }`}
          >
            <span className="material-icons text-base">{l.icon}</span>
            {l.label}
          </button>
        ))}
      </div>

      {lens === 'table' && (
        <>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-40">
              <SelectInput
                value={band}
                onChange={(e) => setBand(e.target.value as Band | '')}
              >
                <option value="">All bands</option>
                {BANDS.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </SelectInput>
            </div>
          </div>

          <Card className="!p-0">
            <QueryState
              isLoading={assets.isLoading && pages.length === 0}
              error={assets.error}
              isEmpty={allAssets.length === 0 && !assets.isLoading}
              emptyProps={{
                icon: 'shield',
                title: 'No scored assets',
                hint: 'Ingest a CycloneDX/CBOM into an inventory source — scoring runs automatically.',
              }}
            >
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-subtle-light dark:text-subtle-dark border-b border-border-light dark:border-border-dark">
                    <th className="px-5 py-3 font-medium">#</th>
                    <th className="px-5 py-3 font-medium">Band</th>
                    <th className="px-5 py-3 font-medium">Score</th>
                    <th className="px-5 py-3 font-medium">Asset</th>
                    <th className="px-5 py-3 font-medium">Algorithm</th>
                    <th className="px-5 py-3 font-medium">Inventory</th>
                    <th className="px-5 py-3 font-medium">Source</th>
                    <th className="px-5 py-3 font-medium">Type</th>
                    <th className="px-5 py-3 font-medium">HNDL</th>
                    <th className="px-5 py-3 font-medium">Target</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-light dark:divide-border-dark">
                  {allAssets.map((a, i) => (
                    <tr
                      key={a.id}
                      onClick={() => navigate(`/assets/${a.id}`)}
                      className="cursor-pointer hover:bg-primary/5"
                    >
                      <td className="px-5 py-3 text-subtle-light dark:text-subtle-dark">
                        {i + 1}
                      </td>
                      <td className="px-5 py-3">
                        <BandBadge band={a.band} />
                      </td>
                      <td className="px-5 py-3 font-semibold text-text-light dark:text-text-dark">
                        {a.score?.toFixed(1)}
                      </td>
                      <td className="px-5 py-3 max-w-[220px]">
                        <div className="font-medium text-text-light dark:text-text-dark truncate">
                          {a.name}
                        </div>
                        <div className="text-xs text-subtle-light dark:text-subtle-dark truncate font-mono">
                          {a.external_ref || a.bom_ref || a.id.slice(0, 8)}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-text-light dark:text-text-dark">
                        {a.algorithm}
                        {a.key_size ? ` ${a.key_size}` : ''}
                      </td>
                      <td className="px-5 py-3 text-subtle-light dark:text-subtle-dark truncate max-w-[160px]">
                        {a.inventory_name || '—'}
                      </td>
                      <td className="px-5 py-3 text-subtle-light dark:text-subtle-dark truncate max-w-[160px]">
                        {a.source_name || '—'}
                      </td>
                      <td className="px-5 py-3 text-subtle-light dark:text-subtle-dark">
                        {a.asset_type}
                      </td>
                      <td className="px-5 py-3 text-subtle-light dark:text-subtle-dark">
                        {a.hndl_window != null ? `${a.hndl_window.toFixed(1)}y` : '—'}
                      </td>
                      <td className="px-5 py-3 text-subtle-light dark:text-subtle-dark font-mono text-xs">
                        {a.recommended_target || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </QueryState>
          </Card>

          {/* Infinite scroll: sentinel auto-loads the next page near the viewport. */}
          <div ref={sentinelRef} className="h-1" />
          <div className="flex items-center justify-center mt-3 text-xs text-subtle-light dark:text-subtle-dark">
            {assets.isFetching && offset > 0
              ? 'Loading more…'
              : allAssets.length > 0
                ? `${allAssets.length} assets loaded${hasMore ? ' — scroll for more' : ''}`
                : ''}
          </div>
        </>
      )}

      {lens === 'treemap' && (
        <Card>
          <TreemapLens orgId={orgId} />
        </Card>
      )}
      {lens === 'sunburst' && (
        <Card>
          <SunburstLens orgId={orgId} />
        </Card>
      )}
      {lens === 'heatmap' && (
        <Card>
          <HeatmapLens orgId={orgId} />
        </Card>
      )}
      {lens === 'graph' && <GraphView orgId={orgId} />}
    </div>
  )
}
